import React, { useState } from 'react';
import { Lock, Globe } from 'lucide-react';

// ============================================================================
// Helpers
// ============================================================================
function formatUrl(url) {
  if (!url) return '';
  // Safari hides the protocol and trailing slash in the address bar
  return String(url).replace(/^https?:\/\//, '').replace(/\/$/, '');
}

function isSecure(url) {
  return typeof url === 'string' && url.startsWith('https://');
}

// ============================================================================
// SafariBrowser — minimal Safari-style frame for a static page screenshot
// Props:
//   url:        string  — the page URL (shown in the address bar)
//   screenshot: string  — an image URL for the page content (any web-loadable src)
// ============================================================================
export function SafariBrowser({ url, screenshot }) {
  const secure = isSecure(url);
  return (
    <>
      <style>{styles}</style>
      <div className="sb-window">
        <div className="sb-chrome">
          <div className="sb-traffic">
            <span className="tl tl-r" />
            <span className="tl tl-y" />
            <span className="tl tl-g" />
          </div>
          <div className="sb-address" title={url || ''}>
            {url
              ? (secure ? <Lock size={11} strokeWidth={2.5} /> : <Globe size={11} strokeWidth={2} />)
              : <Globe size={11} strokeWidth={2} />}
            <span className="sb-url">{formatUrl(url) || 'about:blank'}</span>
          </div>
        </div>
        <div className="sb-content">
          {screenshot
            ? <img src={screenshot} alt={url ? `Screenshot of ${url}` : 'page screenshot'} />
            : <div className="sb-empty"></div>}
        </div>
      </div>
    </>
  );
}

// ============================================================================
// DEMO — sample (url, screenshot) pairs
// Real screenshots will be web URLs; for the artifact demo, SVG data URIs.
// ============================================================================
const svgDataUrl = (svg) => `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;

const LANDING_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 880" preserveAspectRatio="xMidYMin meet">
<rect width="1024" height="880" fill="#fbfaf7"/>
<rect x="0" y="0" width="1024" height="64" fill="#ffffff"/>
<line x1="0" y1="64" x2="1024" y2="64" stroke="#ececec" stroke-width="1"/>
<circle cx="56" cy="32" r="10" fill="#1d1d1f"/>
<text x="80" y="38" font-family="-apple-system, system-ui, sans-serif" font-size="16" font-weight="700" fill="#1d1d1f">acme</text>
<text x="780" y="38" font-family="-apple-system, system-ui, sans-serif" font-size="13" fill="#3c3c43">Product</text>
<text x="850" y="38" font-family="-apple-system, system-ui, sans-serif" font-size="13" fill="#3c3c43">Pricing</text>
<text x="912" y="38" font-family="-apple-system, system-ui, sans-serif" font-size="13" fill="#3c3c43">Docs</text>
<text x="966" y="38" font-family="-apple-system, system-ui, sans-serif" font-size="13" fill="#3c3c43">Login</text>
<text x="512" y="208" font-family="-apple-system, system-ui, sans-serif" font-size="56" font-weight="700" fill="#1d1d1f" text-anchor="middle">Ship faster.</text>
<text x="512" y="270" font-family="-apple-system, system-ui, sans-serif" font-size="56" font-weight="700" fill="#1d1d1f" text-anchor="middle">Ship together.</text>
<text x="512" y="320" font-family="-apple-system, system-ui, sans-serif" font-size="18" fill="#6c6c70" text-anchor="middle">The team workspace built for engineers who actually build things.</text>
<rect x="412" y="354" width="200" height="48" rx="24" fill="#1d1d1f"/>
<text x="512" y="384" font-family="-apple-system, system-ui, sans-serif" font-size="15" font-weight="600" fill="#fff" text-anchor="middle">Get started — it's free</text>
<rect x="60" y="490" width="290" height="240" rx="10" fill="#ffffff" stroke="#ececec"/>
<rect x="84" y="514" width="40" height="40" rx="8" fill="#fff4d6"/>
<text x="84" y="592" font-family="-apple-system, sans-serif" font-size="15" font-weight="600" fill="#1d1d1f">Fast by default</text>
<text x="84" y="616" font-family="-apple-system, sans-serif" font-size="12" fill="#6c6c70">Instant navigation, zero blank</text>
<text x="84" y="632" font-family="-apple-system, sans-serif" font-size="12" fill="#6c6c70">screens, sub-100ms responses.</text>
<rect x="367" y="490" width="290" height="240" rx="10" fill="#ffffff" stroke="#ececec"/>
<rect x="391" y="514" width="40" height="40" rx="8" fill="#d4f4d4"/>
<text x="391" y="592" font-family="-apple-system, sans-serif" font-size="15" font-weight="600" fill="#1d1d1f">Built for teams</text>
<text x="391" y="616" font-family="-apple-system, sans-serif" font-size="12" fill="#6c6c70">Real-time collaboration, audit</text>
<text x="391" y="632" font-family="-apple-system, sans-serif" font-size="12" fill="#6c6c70">trails, granular permissions.</text>
<rect x="674" y="490" width="290" height="240" rx="10" fill="#ffffff" stroke="#ececec"/>
<rect x="698" y="514" width="40" height="40" rx="8" fill="#d4e4fc"/>
<text x="698" y="592" font-family="-apple-system, sans-serif" font-size="15" font-weight="600" fill="#1d1d1f">Honest pricing</text>
<text x="698" y="616" font-family="-apple-system, sans-serif" font-size="12" fill="#6c6c70">Flat per-seat, no surprises,</text>
<text x="698" y="632" font-family="-apple-system, sans-serif" font-size="12" fill="#6c6c70">no enterprise call required.</text>
<rect x="0" y="800" width="1024" height="80" fill="#f5f5f7"/>
<text x="60" y="844" font-family="-apple-system, sans-serif" font-size="11" fill="#8e8e93">© 2026 Acme Inc.</text>
<text x="800" y="844" font-family="-apple-system, sans-serif" font-size="11" fill="#8e8e93">Privacy · Terms · Status</text>
</svg>`;

const DOCS_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 960" preserveAspectRatio="xMidYMin meet">
<rect width="1024" height="960" fill="#ffffff"/>
<rect x="0" y="0" width="1024" height="56" fill="#fafafa"/>
<line x1="0" y1="56" x2="1024" y2="56" stroke="#ececec" stroke-width="1"/>
<text x="40" y="34" font-family="-apple-system, sans-serif" font-size="14" font-weight="700" fill="#1d1d1f">docs.acme.dev</text>
<rect x="400" y="14" width="320" height="28" rx="6" fill="#fff" stroke="#d0d0d4"/>
<text x="416" y="32" font-family="-apple-system, sans-serif" font-size="12" fill="#a0a0a4">Search docs…</text>
<rect x="0" y="56" width="240" height="904" fill="#fafafa"/>
<line x1="240" y1="56" x2="240" y2="960" stroke="#ececec"/>
<text x="24" y="92" font-family="-apple-system, sans-serif" font-size="11" font-weight="700" fill="#6c6c70">GETTING STARTED</text>
<text x="24" y="118" font-family="-apple-system, sans-serif" font-size="13" fill="#1d1d1f">Introduction</text>
<rect x="16" y="128" width="208" height="22" rx="4" fill="#e8f0fc"/>
<text x="24" y="144" font-family="-apple-system, sans-serif" font-size="13" font-weight="500" fill="#1d4faf">Installation</text>
<text x="24" y="170" font-family="-apple-system, sans-serif" font-size="13" fill="#1d1d1f">Quickstart</text>
<text x="24" y="196" font-family="-apple-system, sans-serif" font-size="13" fill="#1d1d1f">Configuration</text>
<text x="24" y="234" font-family="-apple-system, sans-serif" font-size="11" font-weight="700" fill="#6c6c70">CORE CONCEPTS</text>
<text x="24" y="260" font-family="-apple-system, sans-serif" font-size="13" fill="#1d1d1f">Workspaces</text>
<text x="24" y="286" font-family="-apple-system, sans-serif" font-size="13" fill="#1d1d1f">Projects</text>
<text x="24" y="312" font-family="-apple-system, sans-serif" font-size="13" fill="#1d1d1f">Tasks &amp; assignments</text>
<text x="24" y="338" font-family="-apple-system, sans-serif" font-size="13" fill="#1d1d1f">Permissions</text>
<text x="24" y="376" font-family="-apple-system, sans-serif" font-size="11" font-weight="700" fill="#6c6c70">API REFERENCE</text>
<text x="24" y="402" font-family="-apple-system, sans-serif" font-size="13" fill="#1d1d1f">Authentication</text>
<text x="24" y="428" font-family="-apple-system, sans-serif" font-size="13" fill="#1d1d1f">Webhooks</text>
<text x="24" y="454" font-family="-apple-system, sans-serif" font-size="13" fill="#1d1d1f">Rate limits</text>
<text x="280" y="110" font-family="-apple-system, sans-serif" font-size="11" fill="#8e8e93">Getting started › Installation</text>
<text x="280" y="152" font-family="-apple-system, sans-serif" font-size="34" font-weight="700" fill="#1d1d1f">Installation</text>
<text x="280" y="194" font-family="-apple-system, sans-serif" font-size="14" fill="#3c3c43">Install the Acme CLI to get started. Requires Node 20 or higher.</text>
<text x="280" y="244" font-family="-apple-system, sans-serif" font-size="16" font-weight="600" fill="#1d1d1f">Using npm</text>
<rect x="280" y="262" width="680" height="56" rx="6" fill="#0d1117"/>
<text x="300" y="296" font-family="ui-monospace, Menlo, monospace" font-size="13" fill="#e0e0e3">$ npm install -g @acme/cli</text>
<text x="280" y="368" font-family="-apple-system, sans-serif" font-size="16" font-weight="600" fill="#1d1d1f">Using Homebrew</text>
<rect x="280" y="386" width="680" height="56" rx="6" fill="#0d1117"/>
<text x="300" y="420" font-family="ui-monospace, Menlo, monospace" font-size="13" fill="#e0e0e3">$ brew install acme</text>
<text x="280" y="492" font-family="-apple-system, sans-serif" font-size="16" font-weight="600" fill="#1d1d1f">Verify the install</text>
<text x="280" y="518" font-family="-apple-system, sans-serif" font-size="14" fill="#3c3c43">Run the version command to confirm:</text>
<rect x="280" y="536" width="680" height="56" rx="6" fill="#0d1117"/>
<text x="300" y="570" font-family="ui-monospace, Menlo, monospace" font-size="13" fill="#e0e0e3">$ acme --version</text>
<text x="280" y="616" font-family="ui-monospace, Menlo, monospace" font-size="13" fill="#6c6c70">acme/3.4.2 darwin-arm64 node-v22.1.0</text>
<rect x="280" y="660" width="680" height="80" rx="6" fill="#fff4d6" stroke="#e8c87a"/>
<text x="300" y="688" font-family="-apple-system, sans-serif" font-size="13" font-weight="600" fill="#6a4400">⚠ Heads up</text>
<text x="300" y="710" font-family="-apple-system, sans-serif" font-size="13" fill="#6a4400">On Windows, install via WSL2. Native Windows is not yet supported.</text>
<text x="300" y="726" font-family="-apple-system, sans-serif" font-size="13" fill="#6a4400">Track progress in issue #142.</text>
</svg>`;

const SAMPLES = [
  { url: 'https://acme.dev', screenshot: svgDataUrl(LANDING_SVG) },
  { url: 'https://docs.acme.dev/installation', screenshot: svgDataUrl(DOCS_SVG) },
  { url: 'http://insecure-example.test', screenshot: svgDataUrl(LANDING_SVG) },
  { url: 'https://example.com/missing', screenshot: null }
];

export default function App() {
  const [idx, setIdx] = useState(0);
  const sample = SAMPLES[idx];
  return (
    <>
      <style>{demoStyles}</style>
      <div className="demo-stage">
        <div className="demo-bar">
          <span className="demo-label">demo:</span>
          {SAMPLES.map((s, i) => (
            <button
              key={i}
              className={`demo-btn ${idx === i ? 'demo-btn-active' : ''}`}
              onClick={() => setIdx(i)}
            >
              {s.screenshot ? formatUrl(s.url) : 'no-screenshot'}
            </button>
          ))}
        </div>
        <SafariBrowser url={sample.url} screenshot={sample.screenshot} />
        <div className="demo-caption">
          <code>&lt;SafariBrowser url=&#123;url&#125; screenshot=&#123;imageUrl&#125; /&gt;</code>
        </div>
      </div>
    </>
  );
}

// ============================================================================
// Styles
// ============================================================================
const styles = `
.sb-window {
  width: 100%;
  height: 100%;
  background: #fff;
  border: 1px solid #c8c8cc;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow:
    0 0 0 1px rgba(0,0,0,0.04),
    0 30px 60px -20px rgba(0,0,0,0.55),
    0 16px 36px -10px rgba(0,0,0,0.35);
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif;
  color: #1d1d1f;
}

.sb-chrome {
  height: 44px;
  background: linear-gradient(180deg, #ececec, #e2e2e2);
  border-bottom: 1px solid #c8c8cc;
  display: flex;
  align-items: center;
  padding: 0 14px;
  position: relative;
  flex-shrink: 0;
}

.sb-traffic { display: flex; gap: 8px; }
.sb-window .tl {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 0.5px solid rgba(0,0,0,0.15);
}
.sb-window .tl-r { background: #ff5f57; }
.sb-window .tl-y { background: #febc2e; }
.sb-window .tl-g { background: #28c840; }

.sb-address {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(0, 0, 0, 0.08);
  padding: 4px 16px;
  border-radius: 7px;
  min-width: 320px;
  max-width: 560px;
  font-size: 13px;
  color: #1d1d1f;
  box-shadow: inset 0 0.5px 0 rgba(255,255,255,0.5);
}
.sb-address svg { color: #3c3c43; flex-shrink: 0; }

.sb-url {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  letter-spacing: -0.01em;
}

.sb-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  background: #ffffff;
  position: relative;
}
.sb-content::-webkit-scrollbar { width: 10px; }
.sb-content::-webkit-scrollbar-track { background: transparent; }
.sb-content::-webkit-scrollbar-thumb { background: #c8c8cc; border-radius: 5px; border: 2px solid #fff; }
.sb-content img {
  display: block;
  width: 100%;
  height: auto;
}

.sb-empty {
  padding: 80px 40px;
  text-align: center;
  color: #8e8e93;
  font-style: italic;
  font-size: 14px;
}
`;

const demoStyles = `
.demo-stage {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  gap: 14px;
  background: linear-gradient(180deg, #2a2a2c 0%, #1c1c1e 100%);
}
.demo-bar {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 1024px;
}
.demo-label {
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 11px;
  color: #a0a0a4;
}
.demo-btn {
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.15);
  color: #e0e0e3;
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 10.5px;
  padding: 3px 8px;
  border-radius: 4px;
  cursor: pointer;
}
.demo-btn:hover { background: rgba(255,255,255,0.15); color: #fff; }
.demo-btn-active { background: #007aff; border-color: #007aff; color: #fff; }
.demo-caption {
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 11px;
  color: #a0a0a4;
}
.demo-caption code {
  background: rgba(255,255,255,0.08);
  padding: 1px 6px;
  border-radius: 3px;
  color: #e0e0e3;
}
`;
